import { LoadingArea } from '@consigli/facade';
import { useProjectId, useServiceName } from '@consigli/hooks';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigationHistory } from '@/features/package-creation/use-navigation-history';
import { Nav } from '@/layouts/nav';

export enum TabType {
  CREATE_PACKAGE = 'CREATE_PACKAGE',
  COMPARE_PACKAGE = 'COMPARE_PACKAGE',
}

interface InsightWizardLayoutProps {
  isProcessing: boolean;
  messageHeader?: JSX.Element;
  isUploading: boolean;
  progress: number;
  tabContent?: {
    [key in TabType]?: JSX.Element;
  };
}

type TabProps = {
  label: string;
  isActive: boolean;
  onClick: () => void;
};

const WizardTab: React.FC<TabProps> = ({ label, isActive, onClick }) => (
  <div
    className={clsx(
      'flex mx-2 pb-1 lg:pb-0 items-center justify-center text-center text-day-neutral-dark border-b-[3px] transition-colors duration-300 cursor-pointer',
      isActive ? 'border-day-neutral-dark' : 'border-transparent hover:border-day-neutral-dark',
    )}
    onClick={onClick}
    aria-hidden="true"
  >
    <div className="w-full whitespace-nowrap text-xl">{label}</div>
  </div>
);

export const WizardLayout = ({
  isProcessing,
  messageHeader,
  isUploading,
  progress,
  tabContent = {},
}: InsightWizardLayoutProps) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const { wizardBackButtonClick } = useNavigationHistory();
  const [activeTab, setActiveTab] = useState<TabType>(TabType.CREATE_PACKAGE);

  const tabs = useMemo(() => {
    return Object.keys(tabContent).map((key) => {
      return {
        id: key as TabType,
        label: () => {
          if (key === TabType.CREATE_PACKAGE) return t(`package-creation.new.${serviceName}`);
          if (key === TabType.COMPARE_PACKAGE)
            return t('package-creation.compare-with-existing-package');
          return `Tab ${key}`;
        },
      };
    });
  }, [tabContent, t, serviceName]);

  const handleTabClick = useCallback((tabId: TabType) => {
    setActiveTab(tabId);
  }, []);

  const renderActiveTabContent = useCallback(() => {
    return tabContent[activeTab];
  }, [tabContent, activeTab]);

  return (
    <div className="w-full flex flex-col min-h-[calc(100vh-theme(space.14))] bg-day-light-4">
      <Nav projectId={projectId} handleClick={wizardBackButtonClick} />
      <div className="flex flex-col flex-1 items-center px-4 py-12 w-full sm:w-[50rem] mx-auto">
        <div className="mb-10 font-semibold text-2xl text-day-neutral-dark">
          <div className="flex">
            {tabs.map((tab) => (
              <WizardTab
                key={tab.id}
                label={tab.label()}
                isActive={activeTab === tab.id}
                onClick={() => handleTabClick(tab.id)}
              />
            ))}
          </div>
        </div>
        <>
          {isUploading || isProcessing ? (
            <LoadingArea
              className="mt-20"
              title={t('document-upload.uploading')}
              message={progress < 1 ? t('document-upload.initialising') : `${progress}%`}
            />
          ) : (
            <>
              {messageHeader && <div className="mb-4 w-full">{messageHeader}</div>}
              {renderActiveTabContent()}
            </>
          )}
        </>
      </div>
    </div>
  );
};
